import React, { useState, useEffect } from 'react';
import { transactionService } from '../services/transactionService';
import { profileService } from '../services/profileService';
import type { Transaction } from '../types/api';

interface TransactionDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  transactionId: string;
}

const TransactionDetailsModal: React.FC<TransactionDetailsModalProps> = ({
  isOpen,
  onClose,
  transactionId
}) => {
  const [transaction, setTransaction] = useState<Transaction | null>(null);
  const [displayCurrency, setDisplayCurrency] = useState<string>('MYR');

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (isOpen && transactionId) {
      fetchTransactionDetails();
    }
  }, [isOpen, transactionId]);

  const fetchTransactionDetails = async () => {
    try {
      setIsLoading(true);
      setError('');

      const response = await transactionService.getTransaction(transactionId);

      if (response.success && response.data) {
        setTransaction(response.data);

        // Determine currency from profile (bank)
        let code: string | undefined;
        const pid = response.data.profileId;
        if (pid && typeof pid === 'object') {
          const p: any = pid as any;
          const bank = (p?.accbank || '').toLowerCase();
          if (bank.includes('kbankpromptpay')) code = 'THB';
          else if (p?.currency) code = String(p.currency).toUpperCase();
        } else if (pid && typeof pid === 'string') {
          try {
            const profRes = await profileService.getProfile(pid);
            if (profRes.success && profRes.data) {
              const p: any = profRes.data as any;
              const bank = (p?.accbank || '').toLowerCase();
              if (bank.includes('kbankpromptpay')) code = 'THB';
              else if (p?.currency) code = String(p.currency).toUpperCase();
            }
          } catch (e) {
            // ignore profile load errors; fallback to MYR below
          }
        }
        setDisplayCurrency(code || 'MYR');
      } else {
        setError(response.message || 'Failed to fetch transaction details');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to fetch transaction details');
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return transactionService.formatAmount(amount, displayCurrency || 'MYR');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-MY', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
      case 'done':
        return 'bg-green-100 text-green-800';
      case 'pending':
      case 'waiting':
      case 'pendingpayment':
        return 'bg-yellow-100 text-yellow-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'failed':
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'userlogin':
        return 'bg-blue-100 text-blue-800';
      case 'selectaccount':
      case 'account_selection_required':
      case 'SELECTACCOUNT':
        return 'bg-purple-100 text-purple-800';
      case 'waitapproval':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
      case 'done':
        return '✅';
      case 'pending':
      case 'waiting':
      case 'pendingpayment':
        return '⏳';
      case 'processing':
        return '🔄';
      case 'failed':
      case 'rejected':
        return '❌';
      case 'userlogin':
        return '🔐';
      case 'selectaccount':
      case 'account_selection_required':
      case 'SELECTACCOUNT':
        return '🏦';
      case 'waitapproval':
        return '⏰';
      default:
        return '❓';
    }
  };

  const getTransactionTypeIcon = (transactionType: string) => {
    switch (transactionType?.toLowerCase()) {
      case 'fpx':
        return '🏦';
      case 'qr':
        return '📱';
      case 'custom':
        return '💳';
      default:
        return '❓';
    }
  };

  const getTransactionTypeDisplayName = (transactionType: string) => {
    switch (transactionType?.toLowerCase()) {
      case 'fpx':
        return 'FPX Bank Transfer';
      case 'qr':
        return 'QR Payment';
      case 'custom':
        return 'Custom Payment';
      default:
        return 'Unknown';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Transaction Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Loading transaction details...</span>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
              <strong>Error:</strong> {error}
            </div>
          ) : transaction ? (
            <div className="space-y-6">
              {/* <div>{JSON.stringify(transaction)}</div> */}
              <div>{
                  transaction.transactiontype == 'fpx' && transaction.transaction == 'receiving' ? 
                  <div><button onClick={() => window.open(`/deposit/${transaction._id}`, '_blank')} className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600">View Transaction</button></div> : null
                }
              </div>
              {/* Status and Basic Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">{getStatusIcon(transaction.statusTxt)}</span>
                    <div>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(transaction.statusTxt)}`}>
                        {transaction.statusTxt.toUpperCase()}
                      </span>
                      <p className="text-sm text-gray-600 mt-1">
                        {transaction.transaction === 'receiving' ? 'Receiving Payment' : 'Transfer Payment'}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-gray-900">
                      {formatCurrency(transaction.amount)}
                    </div>
                    <p className="text-sm text-gray-600">Amount</p>
                  </div>
                </div>
              </div>

              {/* Transaction Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">Transaction Information</h3>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Transaction ID</label>
                      <p className="text-sm text-gray-900 font-mono">{transaction._id}</p>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium text-gray-500">Type</label>
                      <p className="text-sm text-gray-900">
                        {transaction.transaction === 'receiving' ? '📥 Receiving' : '📤 Transfer'}
                      </p>
                    </div>

                    {transaction.transactiontype && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Payment Method</label>
                        <p className="text-sm text-gray-900">
                          {getTransactionTypeIcon(transaction.transactiontype)} {getTransactionTypeDisplayName(transaction.transactiontype)}
                        </p>
                      </div>
                    )}

                    {transaction.clientId && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Client ID</label>
                        <p className="text-sm text-gray-900">{transaction.clientId}</p>
                      </div>
                    )}
                    
                    {transaction.paymentId && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Payment ID</label>
                        <p className="text-sm text-gray-900 font-mono">{transaction.paymentId}</p>
                      </div>
                    )}
                    
                    {transaction.paymentReference && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Payment Reference</label>
                        <p className="text-sm text-gray-900">{transaction.paymentReference}</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">Timing Information</h3>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Created At</label>
                      <p className="text-sm text-gray-900">{formatDate(transaction.createdAt)}</p>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium text-gray-500">Last Updated</label>
                      <p className="text-sm text-gray-900">{formatDate(transaction.updatedAt)}</p>
                    </div>
                    
                    {transaction.timeoutSeconds && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Timeout Duration</label>
                        <p className="text-sm text-gray-900">{transaction.timeoutSeconds} seconds</p>
                      </div>
                    )}
                    
                    {transaction.timeoutAt && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Timeout At</label>
                        <p className="text-sm text-gray-900">{formatDate(transaction.timeoutAt)}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Remark */}
              {transaction.remark && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Remark</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-700">{transaction.remark}</p>
                  </div>
                </div>
              )}

              {/* QR Content */}
              {transaction.qrContent && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">QR Content</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-700 font-mono break-all">{transaction.qrContent}</p>
                  </div>
                </div>
              )}

              {/* Transaction Logs */}
              {transaction.logs && transaction.logs.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Transaction History</h3>
                  <div className="space-y-3">
                    {transaction.logs.map((log, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="text-sm text-gray-900">{log.message}</p>
                            {log.remark && (
                              <p className="text-sm text-gray-600 mt-1">{log.remark}</p>
                            )}
                          </div>
                          <div className="text-sm text-gray-500 ml-4">
                            {formatDate(log.timestamp)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransactionDetailsModal;
